import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Eye, Search, ArrowUpDown, Download, Phone, Globe } from 'lucide-react';

import { API_URL } from '../../utils/api';
import { getErrorMessage } from '../../utils/errors';

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordRes, custRes] = await Promise.all([
        axios.get(`${API_URL}/admin/orders`),
        axios.get(`${API_URL}/admin/customers`)
      ]);
      setOrders(ordRes.data);
      setCustomers(custRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCustomer = (userId) => customers.find(c => c.id === userId);
  const getCustomerEmail = (userId) => getCustomer(userId)?.email || 'Unknown';
  const getCustomerName = (userId) => getCustomer(userId)?.name || 'Unknown';

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/orders/export`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'orders.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Orders exported successfully");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to export orders"));
    }
  };

  const filteredOrders = orders
    .filter(order => {
      const q = search.toLowerCase();
      const matchesSearch =
        order.id.toLowerCase().includes(q) ||
        (order.order_numbers || []).some((n) => n.toLowerCase().includes(q)) ||
        getCustomerEmail(order.user_id).toLowerCase().includes(q) ||
        getCustomerName(order.user_id).toLowerCase().includes(q);
      const matchesSource = sourceFilter === 'all' || (order.source || 'web') === sourceFilter;
      return matchesSearch && matchesSource;
    })
    .sort((a, b) => {
      const value = (o) => (sortConfig.key === 'order_number' ? o.order_numbers?.[0] || '' : o[sortConfig.key]);
      if (value(a) < value(b)) return sortConfig.direction === 'asc' ? -1 : 1;
      if (value(a) > value(b)) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getSourceBadge = (source) => {
    if (source === 'ivr') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <Phone className="h-3 w-3" /> IVR
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
        <Globe className="h-3 w-3" /> Web
      </span>
    );
  };

  const ivrCount = orders.filter(o => (o.source || 'web') === 'ivr').length;
  const webCount = orders.filter(o => (o.source || 'web') === 'web').length;

  return (
    <div className="min-h-screen bg-paper">
      <AdminSidebar />
      <div className="ml-64 p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-serif text-3xl font-bold text-ink mb-2">Orders</h1>
          <p className="text-ink/60 mb-8">All orders from web and IVR (phone)</p>

          <div className="flex gap-4 mb-6 items-center flex-wrap">
            <div className="relative w-full sm:max-w-md flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden="true" />
                <Input
                    aria-label="Search orders"
                    placeholder="Search by order number, name, or email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                />
            </div>
            <div className="flex gap-2">
              <Button
                variant={sourceFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSourceFilter('all')}
              >
                All ({orders.length})
              </Button>
              <Button
                variant={sourceFilter === 'web' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSourceFilter('web')}
              >
                <Globe className="h-3 w-3 mr-1" /> Web ({webCount})
              </Button>
              <Button
                variant={sourceFilter === 'ivr' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSourceFilter('ivr')}
              >
                <Phone className="h-3 w-3 mr-1" /> IVR ({ivrCount})
              </Button>
            </div>
            <Button variant="outline" onClick={handleExport} className="ml-auto">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          </div>

          <div className="text-sm text-ink/50 mb-4">
            Showing {filteredOrders.length} of {orders.length} orders
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div></div>
          ) : (
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead onClick={() => handleSort('order_number')} className="cursor-pointer hover:bg-gray-50">
                        Order # <ArrowUpDown className="inline h-3 w-3 ml-1" />
                    </TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead onClick={() => handleSort('total_amount')} className="cursor-pointer hover:bg-gray-50">
                        Amount <ArrowUpDown className="inline h-3 w-3 ml-1" />
                    </TableHead>
                    <TableHead onClick={() => handleSort('status')} className="cursor-pointer hover:bg-gray-50">
                        Status <ArrowUpDown className="inline h-3 w-3 ml-1" />
                    </TableHead>
                    <TableHead onClick={() => handleSort('source')} className="cursor-pointer hover:bg-gray-50">
                        Source <ArrowUpDown className="inline h-3 w-3 ml-1" />
                    </TableHead>
                    <TableHead onClick={() => handleSort('created_at')} className="cursor-pointer hover:bg-gray-50">
                        Date <ArrowUpDown className="inline h-3 w-3 ml-1" />
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-ink/40">
                        {search || sourceFilter !== 'all' ? 'No orders match your filters' : 'No orders found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="font-mono text-sm">
                          {order.order_numbers?.length ? order.order_numbers.join(', ') : `${order.id.slice(0, 8)}...`}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{getCustomerName(order.user_id)}</div>
                          <div className="text-xs text-ink/50">{getCustomerEmail(order.user_id)}</div>
                        </TableCell>
                        <TableCell className="font-medium">${order.total_amount?.toFixed(2)}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </TableCell>
                        <TableCell>
                          {getSourceBadge(order.source || 'web')}
                        </TableCell>
                        <TableCell className="text-ink/60">{new Date(order.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <Link to={`/admin/orders/${order.id}`}>
                            <Button size="sm" variant="ghost">
                              <Eye className="h-4 w-4 mr-1" /> View
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
